/**
 * GST for one line item, mirroring the server's calculateItemTax.
 *
 * The forms used to apply the raw rates and ignore threshold_limit /
 * threshold_logic entirely, so a rule like "GST 18% with 7500 Exemption"
 * showed 18% while entering an amount under the limit — and the saved
 * document, computed by the backend, correctly carried no GST at all. The
 * preview and the printed document disagreed.
 *
 * Source of truth: creditDebitNoteController.calculateItemTax and
 * invoiceController's identical copy. Keep the branches in step with them.
 *
 * @param {number} baseAmount
 * @param {object|null} taxRule  needs rate_cgst/rate_sgst/rate_igst and,
 *                               when thresholded, threshold_limit/threshold_logic
 * @returns {{cgst:number, sgst:number, igst:number, total:number}}
 */
export const calculateItemTax = (baseAmount, taxRule) => {
    const zero = { cgst: 0, sgst: 0, igst: 0, total: 0 };
    if (!taxRule) return zero;

    const base = parseFloat(baseAmount) || 0;
    const rateCgst = parseFloat(taxRule.rate_cgst || 0);
    const rateSgst = parseFloat(taxRule.rate_sgst || 0);
    const rateIgst = parseFloat(taxRule.rate_igst || 0);
    if (rateCgst + rateSgst + rateIgst <= 0) return zero;

    const thresholdLimit = parseFloat(taxRule.threshold_limit || 0);
    let taxableAmount = base;

    switch (taxRule.threshold_logic) {
        case 'EXEMPT_BELOW':
            // Under the limit the whole line is exempt, not just the shortfall.
            if (base < thresholdLimit) return zero;
            taxableAmount = base;
            break;
        case 'TAX_EXCESS':
            taxableAmount = Math.max(0, base - thresholdLimit);
            break;
        case 'TAX_ALL':
        default:
            taxableAmount = base;
            break;
    }

    const round2 = (n) => Math.round(n * 100) / 100;

    // A supply is either intra-state (CGST + SGST) or inter-state (IGST), never
    // both. A rule with rates in both columns was charging all three. Intra-state
    // wins; the IGST half of a mixed rule is ignored. Mirrors the server.
    const isIntraState = rateCgst > 0 || rateSgst > 0;

    const cgst = round2((taxableAmount * rateCgst) / 100);
    const sgst = round2((taxableAmount * rateSgst) / 100);
    const igst = isIntraState ? 0 : round2((taxableAmount * rateIgst) / 100);

    return { cgst, sgst, igst, total: round2(cgst + sgst + igst) };
};
